/**
 * Asiento Contable Form - Finance 360
 * Create/Edit accounting entries with AI suggestions
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BookOpen,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Search,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import ConfirmModal from '../../components/ConfirmModal';
import ModalExcedido from '../presupuesto/ModalExcedido';

// Formatear número con separador de miles
const formatNumber = (value) => {
  if (value === null || value === undefined || value === '' || value === 0) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parsear número formateado a valor numérico
const parseFormattedNumber = (value) => {
  if (!value || value === '') return 0;
  // Remover separadores de miles y convertir coma decimal a punto
  const cleaned = value.toString().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// Input numérico con buffer local — no re-formatea mientras el user tipea
// (eso movía el cursor al final con cada keystroke). Formatea solo al perder foco.
function NumericInput({ value, onChange, disabled, placeholder, className }) {
  const [localValue, setLocalValue] = useState(() => (!value ? '' : formatNumber(value)));
  const [focused, setFocused] = useState(false);

  // Si el value externo cambia y NO estamos editando, refrescar el display
  useEffect(() => {
    if (!focused) {
      setLocalValue(!value ? '' : formatNumber(value));
    }
  }, [value, focused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      onFocus={(e) => {
        setFocused(true);
        // Al entrar, mostrar el valor "crudo" para edición libre (sin separadores de miles)
        // Y seleccionar todo así un nuevo número reemplaza fácilmente.
        const raw = !value ? '' : String(value).replace('.', ',');
        setLocalValue(raw);
        // Defer select para después del setState
        setTimeout(() => e.target.select?.(), 0);
      }}
      onChange={(e) => {
        // Aceptamos solo dígitos, una coma decimal y puntos como separador de miles
        const raw = e.target.value.replace(/[^\d.,]/g, '');
        setLocalValue(raw);
        onChange(parseFormattedNumber(raw));
      }}
      onBlur={() => {
        setFocused(false);
        setLocalValue(!value ? '' : formatNumber(value));
      }}
    />
  );
}

export default function AsientoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentEmisor } = useAuthStore();
  const isEditing = Boolean(id);
  const copyFromId = searchParams.get('copyFrom'); // ?copyFrom=<id> → precargar como nuevo

  const [loading, setLoading] = useState(isEditing || Boolean(copyFromId));
  const [saving, setSaving] = useState(false);
  const [excedido, setExcedido] = useState(null); // 409 del control del gasto: { error, controlPresupuesto, puedeExceder }
  const [cuentas, setCuentas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const [editingCuentaIndex, setEditingCuentaIndex] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning' });

  // Form state
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    tipo: 'manual',
    concepto: '',
    estado: 'borrador'
  });

  const [lineas, setLineas] = useState([
    { cuenta_id: null, cuenta_codigo: '', cuenta_nombre: '', descripcion: '', debito: 0, credito: 0 },
    { cuenta_id: null, cuenta_codigo: '', cuenta_nombre: '', descripcion: '', debito: 0, credito: 0 }
  ]);

  // Load data
  useEffect(() => {
    if (currentEmisor) {
      loadCuentas();
      if (isEditing) {
        loadAsiento();
      } else if (copyFromId) {
        loadAsientoForCopy(copyFromId);
      }
    }
  }, [currentEmisor, id, copyFromId]);

  const loadCuentas = async () => {
    try {
      const response = await contabilidadApi.getCuentas({ flat: true, soloMovimiento: true });
      setCuentas(response.data.data);
    } catch (error) {
      console.error('Error loading cuentas:', error);
    }
  };

  const loadAsiento = async () => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getAsiento(id);
      const asiento = response.data.data;

      setFormData({
        fecha: asiento.fecha.split('T')[0],
        tipo: asiento.tipo,
        concepto: asiento.concepto,
        estado: asiento.estado
      });

      setLineas(asiento.lineas.map(l => ({
        id: l.id,
        cuenta_id: l.cuentaId,
        cuenta_codigo: l.cuentaCodigo,
        cuenta_nombre: l.cuentaNombre,
        descripcion: l.descripcion || '',
        debito: parseFloat(l.debito) || 0,
        credito: parseFloat(l.credito) || 0
      })));
    } catch (error) {
      console.error('Error loading asiento:', error);
      toast.error('Error al cargar el asiento');
      navigate('/contabilidad/asientos');
    } finally {
      setLoading(false);
    }
  };

  // Carga un asiento existente como BASE para uno nuevo: copia tipo/concepto/líneas,
  // pero pone fecha = hoy, estado = borrador y descarta IDs (para que el backend cree todo).
  const loadAsientoForCopy = async (sourceId) => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getAsiento(sourceId);
      const asiento = response.data.data;

      setFormData({
        fecha: new Date().toISOString().split('T')[0],
        tipo: asiento.tipo === 'automatico' ? 'manual' : asiento.tipo, // no se copia como auto
        concepto: `Copia de: ${asiento.concepto || `Asiento #${asiento.numero || sourceId}`}`,
        estado: 'borrador'
      });

      setLineas(asiento.lineas.map(l => ({
        // sin id → el backend crea líneas nuevas
        cuenta_id: l.cuentaId,
        cuenta_codigo: l.cuentaCodigo,
        cuenta_nombre: l.cuentaNombre,
        descripcion: l.descripcion || '',
        debito: parseFloat(l.debito) || 0,
        credito: parseFloat(l.credito) || 0
      })));

      toast.success('Asiento copiado — revise y guarde');
    } catch (error) {
      console.error('Error copiando asiento:', error);
      toast.error('Error al copiar el asiento');
      navigate('/contabilidad/asientos');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = lineas.reduce((acc, linea) => ({
    debito: acc.debito + (parseFloat(linea.debito) || 0),
    credito: acc.credito + (parseFloat(linea.credito) || 0)
  }), { debito: 0, credito: 0 });

  const isBalanced = Math.abs(totals.debito - totals.credito) < 0.01;

  // Handle cuenta search
  const handleCuentaSearch = useCallback((query, lineIndex) => {
    setSearchQuery(query);
    setActiveLineIndex(lineIndex);

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const results = cuentas.filter(c =>
      c.codigo.toLowerCase().includes(query.toLowerCase()) ||
      c.nombre.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10);

    setSearchResults(results);
  }, [cuentas]);

  const selectCuenta = (cuenta, lineIndex) => {
    const newLineas = [...lineas];
    newLineas[lineIndex] = {
      ...newLineas[lineIndex],
      cuenta_id: cuenta.id,
      cuenta_codigo: cuenta.codigo,
      cuenta_nombre: cuenta.nombre
    };
    setLineas(newLineas);
    setSearchResults([]);
    setActiveLineIndex(null);
    setEditingCuentaIndex(null);
    setSearchQuery('');
  };

  const startEditingCuenta = (index) => {
    setEditingCuentaIndex(index);
    setActiveLineIndex(index);
    setSearchQuery('');
    setSearchResults([]);
  };

  const cancelEditingCuenta = (index) => {
    // Only cancel if we have a cuenta selected
    if (lineas[index].cuenta_id) {
      setEditingCuentaIndex(null);
      setActiveLineIndex(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const updateLinea = (index, field, value) => {
    const newLineas = [...lineas];
    newLineas[index] = { ...newLineas[index], [field]: value };
    setLineas(newLineas);
  };

  const addLinea = () => {
    setLineas([...lineas, {
      cuenta_id: null,
      cuenta_codigo: '',
      cuenta_nombre: '',
      descripcion: '',
      debito: 0,
      credito: 0
    }]);
  };

  const removeLinea = (index) => {
    if (lineas.length <= 2) {
      toast.error('Un asiento debe tener al menos 2 líneas');
      return;
    }
    setLineas(lineas.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e, motivoExceso = null) => {
    e?.preventDefault?.();

    // Validations
    if (!formData.concepto.trim()) {
      toast.error('Ingrese un concepto para el asiento');
      return;
    }

    const validLineas = lineas.filter(l => l.cuenta_id && (l.debito > 0 || l.credito > 0));
    if (validLineas.length < 2) {
      toast.error('El asiento debe tener al menos 2 líneas con cuenta y monto');
      return;
    }

    if (!isBalanced) {
      toast.error('El asiento debe estar cuadrado (débitos = créditos)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        lineas: validLineas.map(l => ({
          cuentaId: l.cuenta_id,
          descripcion: l.descripcion,
          debito: parseFloat(l.debito) || 0,
          credito: parseFloat(l.credito) || 0
        })),
        motivoExceso: motivoExceso || undefined
      };

      if (isEditing) {
        await contabilidadApi.updateAsiento(id, payload);
      } else {
        const r = await contabilidadApi.createAsiento(payload);
        // Control del gasto (P9): en modo avisar, el asiento se guarda y se dice que excede.
        const cp = r.data?.data?.controlPresupuesto;
        if (cp?.resultado === 'aviso') toast(`Excede el presupuesto: ${(cp.mensajes || [])[0] || 'revise el control del gasto'}`, { icon: '⚠️', duration: 6000 });
      }

      setExcedido(null);
      toast.success(isEditing ? 'Asiento actualizado' : 'Asiento creado');
      navigate('/contabilidad/asientos');
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.codigo === 'PRESUPUESTO_EXCEDIDO') {
        setExcedido(error.response.data);
        return;
      }
      toast.error(error.response?.data?.error || 'Error al guardar el asiento');
    } finally {
      setSaving(false);
    }
  };

  const handleContabilizar = () => {
    if (!isBalanced) {
      toast.error('No se puede contabilizar un asiento descuadrado');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Contabilizar Asiento',
      message: '¿Contabilizar este asiento? Esta acción no se puede deshacer.',
      variant: 'info',
      onConfirm: async () => {
        setSaving(true);
        try {
          await contabilidadApi.contabilizarAsiento(id);
          toast.success('Asiento contabilizado correctamente');
          navigate('/contabilidad/asientos');
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al contabilizar');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return <LoadingPage />;
  }

  const isReadOnly = formData.estado !== 'borrador';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/contabilidad/asientos"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Asiento' : 'Nuevo Asiento'}
            </h1>
            {isEditing && (
              <p className="text-sm text-gray-500">
                Estado: <span className={`font-medium ${
                  formData.estado === 'contabilizado' ? 'text-green-600' :
                  formData.estado === 'anulado' ? 'text-red-600' : 'text-yellow-600'
                }`}>{formData.estado}</span>
              </p>
            )}
          </div>
        </div>

        {isReadOnly && (
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
            <Info className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Asiento de solo lectura</span>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header Fields */}
        <div className="bg-white rounded-xl border p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="manual">Manual</option>
                <option value="ajuste">Ajuste</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Concepto</label>
              <input
                type="text"
                value={formData.concepto}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                disabled={isReadOnly}
                placeholder="Descripción del asiento..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                required
              />
            </div>
          </div>
        </div>

        {/* Lines Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Líneas del Asiento</h3>
            {!isReadOnly && (
              <button
                type="button"
                onClick={addLinea}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                <Plus className="h-4 w-4" />
                Agregar Línea
              </button>
            )}
          </div>

          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-80">
                  Cuenta
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Descripción
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-36">
                  Débito
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-36">
                  Crédito
                </th>
                {!isReadOnly && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16">

                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {lineas.map((linea, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 relative">
                    {isReadOnly ? (
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">{linea.cuenta_codigo}</span>
                        <span className="text-gray-500"> - {linea.cuenta_nombre}</span>
                      </p>
                    ) : linea.cuenta_id && editingCuentaIndex !== index ? (
                      /* Cuenta seleccionada - click para editar */
                      <button
                        type="button"
                        onClick={() => startEditingCuenta(index)}
                        className="w-full px-3 py-2 border border-blue-200 bg-blue-50 rounded-lg text-sm text-left hover:border-blue-400 transition-colors"
                      >
                        <span className="font-medium text-blue-700">{linea.cuenta_codigo}</span>
                        <span className="text-blue-600"> - {linea.cuenta_nombre}</span>
                      </button>
                    ) : (
                      /* Input de búsqueda */
                      <div className="relative">
                        <input
                          type="text"
                          value={activeLineIndex === index ? searchQuery : ''}
                          onChange={(e) => handleCuentaSearch(e.target.value, index)}
                          onFocus={() => {
                            setActiveLineIndex(index);
                            if (!linea.cuenta_id) setEditingCuentaIndex(index);
                          }}
                          onBlur={() => {
                            // Delay to allow click on dropdown
                            setTimeout(() => cancelEditingCuenta(index), 200);
                          }}
                          placeholder="Buscar cuenta..."
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          autoFocus={editingCuentaIndex === index}
                        />

                        {/* Search Results Dropdown */}
                        {activeLineIndex === index && searchResults.length > 0 && (
                          <div className="absolute z-10 w-80 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {searchResults.map((cuenta) => (
                              <button
                                key={cuenta.id}
                                type="button"
                                onClick={() => selectCuenta(cuenta, index)}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 text-sm border-b last:border-0"
                              >
                                <span className="font-medium text-gray-900">{cuenta.codigo}</span>
                                <span className="text-gray-500 ml-2">{cuenta.nombre}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={linea.descripcion}
                      onChange={(e) => updateLinea(index, 'descripcion', e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Descripción..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <NumericInput
                      value={linea.debito}
                      onChange={(v) => updateLinea(index, 'debito', v)}
                      disabled={isReadOnly}
                      placeholder="0,00"
                      className="w-full px-3 py-2 border rounded-lg text-sm text-right focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <NumericInput
                      value={linea.credito}
                      onChange={(v) => updateLinea(index, 'credito', v)}
                      disabled={isReadOnly}
                      placeholder="0,00"
                      className="w-full px-3 py-2 border rounded-lg text-sm text-right focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </td>
                  {!isReadOnly && (
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeLinea(index)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="2" className="px-4 py-3 text-right font-medium text-gray-700">
                  Totales:
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-900">
                  {formatCurrency(totals.debito)}
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-900">
                  {formatCurrency(totals.credito)}
                </td>
                {!isReadOnly && <td></td>}
              </tr>
              <tr>
                <td colSpan="2" className="px-4 py-2 text-right text-sm">
                  Diferencia:
                </td>
                <td colSpan={isReadOnly ? 2 : 3} className="px-4 py-2">
                  <div className={`flex items-center justify-end gap-2 ${
                    isBalanced ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {isBalanced ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="font-medium">Cuadrado</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        <span className="font-medium">
                          {formatCurrency(Math.abs(totals.debito - totals.credito))}
                        </span>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Link
            to="/contabilidad/asientos"
            className="flex items-center gap-2 px-4 py-2 border border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 hover:border-gray-500 transition-colors"
          >
            <XCircle className="h-4 w-4" />
            Cancelar
          </Link>

          <div className="flex items-center gap-3">
            {isEditing && formData.estado === 'borrador' && (
              <button
                type="button"
                onClick={handleContabilizar}
                disabled={saving || !isBalanced}
                className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                Contabilizar
              </button>
            )}

            {!isReadOnly && (
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
              >
                {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                {isEditing ? 'Guardar Cambios' : 'Crear Asiento'}
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Confirm Modal */}
      <ModalExcedido excedido={excedido} que="El asiento" volver="Volver al asiento" columna="Este asiento" saving={saving}
        onCerrar={() => setExcedido(null)} onAutorizar={(m) => handleSubmit(null, m)} />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Confirmar"
      />
    </div>
  );
}
